import React, { useCallback, useLayoutEffect, useRef } from 'react';
import cx from 'classnames';
import SequencingTile from './SequencingTile';
import { LOG, isZone, isLiteral, isDelimiter, trackDensity } from './sequencingConstants';

const PLACE_SIZE_VAR = '--sequencing-place-size';

export function largestPlaceInRow(availableWidth, rowInnerHeight, placeCount, fixedWidth, gap, childCount) {
  if (!placeCount) return 0;
  const widthShare = (availableWidth - fixedWidth - gap * Math.max(childCount - 1, 0)) / placeCount;
  return Math.max(0, Math.floor(Math.min(rowInnerHeight, widthShare)));
}

// The row is max-content, so its own width follows the place size it is being asked to
// produce. The track is measured instead, and each row publishes the side its places should
// use: as tall as the band allows, unless the width cannot pay for it.
function useTrackPlaceSize(trackRef) {
  const apply = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    for (const row of track.querySelectorAll('.sequencing__trackRow')) {
      const cs = getComputedStyle(row);
      const gap = parseFloat(cs.columnGap) || 0;
      const padX = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
      const padY = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
      const children = [...row.children];
      const places = children.filter((child) => child.classList.contains('sequencing__position'));
      if (!places.length) continue;
      const fixedWidth = children
        .filter((child) => !child.classList.contains('sequencing__position'))
        .reduce((total, child) => total + child.getBoundingClientRect().width, 0);
      const side = largestPlaceInRow(
        track.clientWidth - padX, row.clientHeight - padY,
        places.length, fixedWidth, gap, children.length,
      );
      if (side > 0) row.style.setProperty(PLACE_SIZE_VAR, `${side}px`);
    }
  }, [trackRef]);

  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;
    apply();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(apply);
    observer.observe(track);
    return () => observer.disconnect();
  }, [trackRef, apply]);
}

function Literal({ option }) {
  if (option.imageUrl) {
    return <img className="sequencing__literalImage" src={option.imageUrl} alt={option.text || ''} />;
  }
  return <span className="sequencing__literalText">{option.text}</span>;
}

function Delimiter({ option }) {
  if (option.imageUrl) {
    return (
      <img className="sequencing__delimiterImage" src={option.imageUrl} alt={option.text || ''} />
    );
  }
  if (String(option.text || '').trim() !== '') {
    return <span className="sequencing__delimiterText">{option.text}</span>;
  }
  return null;
}

function Position({ occupant, isNext, pointed, ordinal }) {
  return (
    <div
      className={cx('sequencing__position', 'pointerTarget', 'top', {
        'sequencing__position--next': isNext,
        'sequencing__position--filled': !!occupant,
        showPointer: pointed,
      })}
      role="group"
      aria-label={`Place ${ordinal}`}
    >
      {occupant ? (
        <div className="sequencing__positionContents">
          <SequencingTile option={occupant} placed />
        </div>
      ) : null}
    </div>
  );
}

export default function SequencingTrack({
  track,
  zones,
  zoneIndexByKey,
  placements,
  poolByKey,
  hideTrack,
  pointerTarget,
  backgroundImageUrl,
}) {
  const trackRef = useRef(null);
  useTrackPlaceSize(trackRef);

  if (track.length === 0) {
    LOG('track has no drop zones — item.dropZones is empty or missing');
    return <div className={cx('sequencing__track', 'sequencing__track--empty', { hidden: hideTrack })} />;
  }

  const nextZoneKey = zones[placements.length]?.key ?? null;

  return (
    <div
      ref={trackRef}
      className={cx('sequencing__track', {
        hidden: hideTrack,
        'sequencing__track--furnished': !!backgroundImageUrl,
      })}
    >
      {backgroundImageUrl ? (
        <img className="sequencing__trackBackground" src={backgroundImageUrl} alt="" />
      ) : null}
      <div className={cx('sequencing__trackRow', `sequencing__trackRow--${trackDensity(track.length)}`)}>
        {track.map((option, trackIndex) => {
          const key = option.key || `${option.kind}-${trackIndex}`;

          if (isZone(option)) {
            const zoneIndex = zoneIndexByKey.get(option.key) ?? -1;
            const occupantKey = placements[zoneIndex];
            return (
              <Position
                key={key}
                ordinal={zoneIndex + 1}
                occupant={occupantKey ? poolByKey[occupantKey] : null}
                isNext={option.key === nextZoneKey}
                pointed={pointerTarget === option.key}
              />
            );
          }
          if (isDelimiter(option)) {
            return <Delimiter key={key} option={option} />;
          }
          if (isLiteral(option)) {
            return <Literal key={key} option={option} />;
          }
          LOG(`track entry ${trackIndex} has no renderer for kind "${option?.kind}" — dropped`);
          return null;
        })}
      </div>
    </div>
  );
}
